import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, ChevronRight, Copy, Search } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { primaryText, secondaryText, semiBoldText, smallTextSize, tertiaryText, quaternaryText } from '../../../styling/stylingHelper';
import { Platform } from '../../../types/Platforms';
import { SupplierLogo } from '../../ui/SupplierLogo';
import { CountReviewBelowMinProduct } from '../../../services/inventory/inventoryService';
import { fmtQuantity } from './inventoryReviewShared';
import { groupBelowMinBySupplier, ORDER_LIST_OTHER_KEY, OrderListSupplierGroup } from './inventoryOrderList';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** The below-minimum products to turn into a per-supplier order list. */
  products: CountReviewBelowMinProduct[];
  /** Optional Expected Inventory context; Count Review keeps its existing copy. */
  subtitle?: string;
  productNotes?: Record<string, string>;
}

/**
 * Order-list modal for the count review "Below minimum" tab: the below-min products
 * grouped by supplier (order products by platform; customs / unknown platform collapse
 * into one section at the bottom). Each supplier is a collapsible row (closed by default)
 * showing its logo, name and product count; expanding lists each product's needed order
 * (count units) and shortfall in purchase units.
 */
export default function OrderListModal({ isOpen, onClose, products, subtitle, productNotes }: Props) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [copyResult, setCopyResult] = useState<{ key: string; success: boolean; message: string } | null>(null);

  useEffect(() => {
    if (!copyResult) return;
    const timeout = window.setTimeout(() => setCopyResult(null), 3000);
    return () => window.clearTimeout(timeout);
  }, [copyResult]);

  useEffect(() => { setCopyResult(null); }, [isOpen, products, searchQuery]);

  // Lock background page scroll while the drawer is open (same idiom as the
  // cost-price detail modals). Cleanup restores it on close or unmount.
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const groups = useMemo<OrderListSupplierGroup[]>(
    () =>
      groupBelowMinBySupplier(
        searchQuery
          ? products.filter((p) => p.productName.toLowerCase().includes(searchQuery.toLowerCase()))
          : products
      ),
    [products, searchQuery]
  );
  const copySupplierList = async (group: OrderListSupplierGroup) => {
    const supplier = group.key === ORDER_LIST_OTHER_KEY ? t('inventory.orderListOtherSupplier') : group.label;
    const text = group.products.map((product) => {
      const units = product.toOrderUnits != null ? fmtQuantity(product.toOrderUnits) : '—';
      return `${units}x ${product.productName}`;
    }).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopyResult({ key: group.key, success: true, message: t('inventory.orderListCopied', { supplier }) });
    } catch (error) {
      console.error('Failed to copy supplier order list:', error);
      setCopyResult({ key: group.key, success: false, message: t('inventory.orderListCopyFailed') });
    }
  };

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <>
      {/* Backdrop — always rendered but fades in/out */}
      <div
        className={cn(
          'fixed inset-0 z-50 bg-black/60 dark:bg-black/60 transition-opacity duration-300',
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
      />

      {/* Drawer — slides in from the right */}
      <div
        className={cn(
          // Wider than the standard slide-over (half the screen on desktop) — the order
          // list shows full product names, which are long and must not be cut off.
          'fixed inset-y-0 right-0 z-50 flex flex-col w-full max-w-md md:w-1/2 md:max-w-none',
          'bg-white dark:bg-[#0E2B42] shadow-2xl',
          'transform transition-transform duration-300 ease-in-out',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center px-4 py-3 border-b border-[#EAECF0] dark:border-[#2D4F68] flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-lg border border-[#E8E8E9] dark:border-[#13354F] hover:bg-gray-50 dark:hover:bg-[#13354F] transition-colors"
            aria-label={t('inventory.cancel') as string}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="dark:text-[#CBD5E1] text-[#000]" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.8337 10H4.16699" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10.0003 15.8332L4.16699 9.99984L10.0003 4.1665" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        <div className="px-4 py-4">
          <h2 className={cn('text-[24px] leading-[36px] font-semibold', primaryText)}>{t('inventory.orderListTitle')}</h2>
          <p className={cn(smallTextSize, secondaryText, 'mt-1')}>{subtitle ?? t('inventory.orderListSubtitle')}</p>
        </div>

        <div className="px-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#667085] dark:text-[#9CA3AF]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('inventory.searchProducts')}
              className="w-full pl-10 pr-3 h-11 rounded-xl border border-[#D0D5DD] dark:border-[#2D4F68] bg-white dark:bg-[#14344C] text-[14px] leading-5 font-normal text-[#111827] dark:text-[#F8FAFC] placeholder:text-[#667085] dark:placeholder:text-[#9CA3AF] outline-none"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <p role="status" className={cn(smallTextSize, secondaryText, copyResult && 'mb-3')}>
            {copyResult?.message}
          </p>
          {groups.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <p className={cn(smallTextSize, quaternaryText, 'text-center')}>{t('inventory.orderListEmpty')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {groups.map((g) => {
                const open = expanded.has(g.key);
                const isOther = g.key === ORDER_LIST_OTHER_KEY;
                const showLogo = !isOther && !!g.platform && g.platform.toLowerCase() !== 'any_invoice';
                return (
                  <div key={g.key} className={cn('rounded-xl border border-[#EAECF0] dark:border-[#2D4F68] overflow-hidden')}>
                    <div className="flex items-center pr-2">
                      <button type="button" onClick={() => toggle(g.key)} aria-expanded={open} className="min-w-0 flex-1 flex items-center justify-between gap-3 px-3 py-3 text-left">
                        <div className="flex items-center gap-3 min-w-0">
                          {showLogo && (
                            <SupplierLogo platform={g.platform as Platform} alt={g.platform ?? ''} />
                          )}
                          <span className={cn(smallTextSize, semiBoldText, primaryText, 'truncate')}>
                            {isOther ? t('inventory.orderListOtherSupplier') : g.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={cn('text-[12px]', tertiaryText, 'shrink-0')}>
                            {g.products.length}
                          </span>
                          {open ? <ChevronDown className="w-4 h-4 shrink-0 text-[#667085] dark:text-textTertiary-dark" /> : <ChevronRight className="w-4 h-4 shrink-0 text-[#667085] dark:text-textTertiary-dark" />}
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => void copySupplierList(g)}
                        aria-label={t('inventory.orderListCopySupplier', { supplier: isOther ? t('inventory.orderListOtherSupplier') : g.label })}
                        title={t('inventory.orderListCopySupplier', { supplier: isOther ? t('inventory.orderListOtherSupplier') : g.label })}
                        className={cn('w-11 h-11 shrink-0 flex items-center justify-center rounded-lg hover:bg-gray-50 dark:hover:bg-[#13354F] transition-colors', secondaryText)}
                      >
                        {copyResult?.key === g.key && copyResult.success ? <Check className="w-4 h-4" aria-hidden="true" /> : <Copy className="w-4 h-4" aria-hidden="true" />}
                      </button>
                    </div>

                    {open && (
                      <div className="border-t border-[#EAECF0] dark:border-[#2D4F68] dark:bg-[#0A1E2E] py-4">
                        <div className={cn('flex items-center gap-4 px-3', tertiaryText)}>
                          <span className={cn('flex-1 min-w-0', 'text-[14px] font-medium')}>{t('inventory.product')}</span>
                          <span className={cn('w-24 text-right', 'text-[14px] font-medium')}>{t('inventory.colNeededOrder')}</span>
                          <span className={cn('w-20 text-right', 'text-[14px] font-medium')}>{t('inventory.colOrderUnits')}</span>
                        </div>
                        <div className="mt-4 divide-y divide-[#EAECF0] dark:divide-[#2D4F68]">
                          {g.products.map((p) => (
                            <div key={`${p.groupId ?? ''}-${p.productKey}`} className="flex items-start gap-4 px-3 py-2">
                              {/* Full name over as many lines as it needs — never truncated. */}
                              <span className={cn(smallTextSize, primaryText, 'flex-1 min-w-0 break-words')}>{p.productName}
                                {productNotes?.[p.productKey] && <span className={cn('block mt-1 text-xs', tertiaryText)}>{productNotes[p.productKey]}</span>}
                              </span>
                              <span className={cn(smallTextSize, secondaryText, 'w-24 text-right whitespace-nowrap shrink-0')}>
                                {p.toOrder != null ? `${fmtQuantity(p.toOrder)} ${p.countUnit}` : '—'}
                              </span>
                              <span className={cn(smallTextSize, semiBoldText, 'w-20 text-right whitespace-nowrap shrink-0', p.toOrderUnits != null ? 'text-red-600 dark:text-red-400' : secondaryText)}>
                                {p.toOrderUnits != null ? fmtQuantity(p.toOrderUnits) : '—'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
